import React, { createContext, useContext, useState, useEffect } from 'react';

const SalesContext = createContext();

export const useSales = () => {
  const context = useContext(SalesContext);
  if (!context) {
    throw new Error('useSales must be used within a SalesProvider');
  }
  return context;
};

export const SalesProvider = ({ children }) => {
  const [sales, setSales] = useState([]);
  const [salesEnabled, setSalesEnabled] = useState(false);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    salesByMonth: {},
    topProducts: []
  });

  // Charger les données depuis localStorage au démarrage
  useEffect(() => {
    const savedSales = localStorage.getItem('boucherie_sales');
    const savedSalesEnabled = localStorage.getItem('boucherie_sales_enabled');
    
    if (savedSales) {
      setSales(JSON.parse(savedSales));
    }
    
    if (savedSalesEnabled === 'true') {
      setSalesEnabled(true);
    }
  }, []);

  // Sauvegarder les ventes dans localStorage
  useEffect(() => {
    localStorage.setItem('boucherie_sales', JSON.stringify(sales));
  }, [sales]);

  // Sauvegarder l'état d'activation des ventes
  useEffect(() => {
    localStorage.setItem('boucherie_sales_enabled', salesEnabled.toString());
  }, [salesEnabled]);

  // Calculer les statistiques
  useEffect(() => {
    if (sales.length > 0) {
      const totalRevenue = sales.reduce((sum, sale) => sum + sale.total, 0);
      const totalOrders = sales.length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      
      // Calculer les ventes par mois
      const salesByMonth = {};
      sales.forEach(sale => {
        const month = new Date(sale.date).toISOString().substring(0, 7); // YYYY-MM
        if (!salesByMonth[month]) {
          salesByMonth[month] = { count: 0, revenue: 0 };
        }
        salesByMonth[month].count += 1;
        salesByMonth[month].revenue += sale.total;
      });

      // Calculer les produits les plus vendus
      const productSales = {};
      sales.forEach(sale => {
        sale.items.forEach(item => {
          if (!productSales[item.id]) {
            productSales[item.id] = {
              name: item.name,
              quantity: 0,
              revenue: 0
            };
          }
          productSales[item.id].quantity += item.quantity;
          productSales[item.id].revenue += item.price * item.quantity;
        });
      });

      const topProducts = Object.values(productSales)
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 5);

      setStats({
        totalSales: totalRevenue,
        totalRevenue: totalRevenue,
        totalOrders: totalOrders,
        averageOrderValue: Math.round(averageOrderValue * 100) / 100,
        salesByMonth,
        topProducts
      });
    } else {
      setStats({
        totalSales: 0,
        totalRevenue: 0,
        totalOrders: 0,
        averageOrderValue: 0,
        salesByMonth: {},
        topProducts: []
      });
    }
  }, [sales]);

  // Activer les ventes (appelé lors du premier paiement)
  const enableSales = () => {
    setSalesEnabled(true);
  };

  // Enregistrer une vente
  const recordSale = (orderData) => {
    const newSale = {
      id: Date.now(),
      date: new Date().toISOString(),
      customer: orderData.customer || 'Client anonyme',
      items: orderData.items,
      total: orderData.total,
      paymentMethod: orderData.paymentMethod || 'Non spécifié',
      status: 'completed'
    };

    setSales(prevSales => [...prevSales, newSale]);
    
    // Activer automatiquement les ventes lors du premier paiement
    if (!salesEnabled) {
      enableSales();
    }

    return newSale;
  };

  // Obtenir les ventes par période
  const getSalesByPeriod = (startDate, endDate) => {
    return sales.filter(sale => {
      const saleDate = new Date(sale.date);
      return saleDate >= startDate && saleDate <= endDate;
    });
  };

  // Obtenir les statistiques par période
  const getStatsByPeriod = (startDate, endDate) => {
    const periodSales = getSalesByPeriod(startDate, endDate);
    const totalRevenue = periodSales.reduce((sum, sale) => sum + sale.total, 0);
    const totalOrders = periodSales.length;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    return {
      totalRevenue,
      totalOrders,
      averageOrderValue: Math.round(averageOrderValue * 100) / 100
    };
  };

  // Réinitialiser les ventes
  const resetSales = () => {
    setSales([]);
    setSalesEnabled(false);
    localStorage.removeItem('boucherie_sales');
    localStorage.removeItem('boucherie_sales_enabled');
  };

  const value = {
    sales,
    salesEnabled,
    stats,
    enableSales,
    recordSale,
    getSalesByPeriod,
    getStatsByPeriod,
    resetSales
  };

  return (
    <SalesContext.Provider value={value}>
      {children}
    </SalesContext.Provider>
  );
};
